import React from 'react';
import { useTranslation } from 'react-i18next';

const RepeatIcon = ({ size = 22 }: { size?: number }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="m17 2 4 4-4 4" />
    <path d="M3 11v-1a4 4 0 0 1 4-4h14" />
    <path d="m7 22-4-4 4-4" />
    <path d="M21 13v1a4 4 0 0 1-4 4H3" />
  </svg>
);

const ZapIcon = ({ size = 22 }: { size?: number }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
  </svg>
);

const WhatsIcon = ({ size = 22 }: { size?: number }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8z" />
  </svg>
);

const PulseIcon = ({ size = 22 }: { size?: number }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
  </svg>
);

const ShieldIcon = ({ size = 22 }: { size?: number }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const LockIcon = ({ size = 22 }: { size?: number }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

interface CardI18nEntry {
  title: string;
  description: string;
}

interface CardConfig {
  icon: React.ReactNode;
  gradient: string;
}

const CARD_CONFIGS: CardConfig[] = [
  { icon: <RepeatIcon size={22} />, gradient: 'from-gray-900 to-black' },
  { icon: <WhatsIcon size={22} />, gradient: 'from-gray-800 to-black' },
  { icon: <PulseIcon size={22} />, gradient: 'from-gray-700 to-black' },
  { icon: <ZapIcon size={22} />, gradient: 'from-gray-600 to-gray-900' },
  { icon: <ShieldIcon size={22} />, gradient: 'from-gray-700 to-black' },
  { icon: <LockIcon size={22} />, gradient: 'from-gray-800 to-black' },
];

export const AdvancedFeaturesSection = React.memo(() => {
  const { t } = useTranslation();
  const cards = t('landing.advanced.cards', { returnObjects: true }) as CardI18nEntry[];

  return (
    <section id="diferenciais" className="py-24 px-6 bg-gradient-to-b from-gray-700/40 via-white to-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">{t('landing.advanced.eyebrow')}</span>
          <h2
            className="text-3xl md:text-5xl font-medium mt-3 mb-5"
            style={{
              background: 'linear-gradient(to bottom, #000000, #000000, rgba(0, 0, 0, 0.6))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.04em',
            }}
          >
            {t('landing.advanced.titleLine1')}
            <br />
            {t('landing.advanced.titleLine2')}
          </h2>
          <p className="text-base max-w-2xl mx-auto text-gray-500">{t('landing.advanced.subtitle')}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {CARD_CONFIGS.map((cfg, i) => (
            <div key={i} className="group relative rounded-2xl p-6 bg-white border border-gray-100 hover:border-gray-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${cfg.gradient} text-white mb-4 shadow-sm`}>
                {cfg.icon}
              </div>
              <h3 className="text-base font-semibold text-black mb-2" style={{ letterSpacing: '-0.01em' }}>{cards[i].title}</h3>
              <p className="text-sm leading-relaxed text-gray-500">{cards[i].description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
});
AdvancedFeaturesSection.displayName = 'AdvancedFeaturesSection';
